import React, { useEffect, useState, useMemo } from 'react';
import { Table, Tag, Button, Card, Spin, Empty, Drawer, Descriptions, Space, Tooltip, Divider, Typography, Input } from 'antd';
import { PlusOutlined, CheckCircleOutlined, DollarOutlined, StopOutlined, EyeOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../variables/api/axiosInstance';
import { formatDate, formatMontant, isDossierMurOuPlus, STATUT_MAP } from '../../../variables/constants';
import KpiCards from '../../../components/KpiCards';
import TableExportButton from '../../../components/TableExportButton';
import logoLocal from '../../../assets/images/logo.png';
import CreerPrefinancementModal from './modals/CreerPrefinancementModal';
import ValiderPrefinancementModal from './modals/ValiderPrefinancementModal';
import RembourserPrefinancementModal from './modals/RembourserPrefinancementModal';
import AnnulerPrefinancementModal from './modals/AnnulerPrefinancementModal';
import { libellesTraduits } from '../../../i18n/libelles';

const STATUT_PREFINANCEMENT_COLORS = {
    DEMANDE: 'orange',
    VALIDE: 'blue',
    REMBOURSE_PARTIEL: 'cyan',
    REMBOURSE_TOTAL: 'green',
    ANNULE: 'red',
};

const STATUT_PREFINANCEMENT_LABELS = libellesTraduits('dossierFinance:prefinancements.statuts');

export default function OngletPrefinancements({ sinistre, canRedacteur = true, canComptable = true }) {
    const { t } = useTranslation('dossierFinance');
    const [prefinancements, setPrefinancements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [showDetail, setShowDetail] = useState(false);
    const [showCreer, setShowCreer] = useState(false);
    const [showValider, setShowValider] = useState(null);
    const [showRembourser, setShowRembourser] = useState(null);
    const [showAnnuler, setShowAnnuler] = useState(null);
    const [search, setSearch] = useState('');

    const sinistreId = sinistre?.sinistreTrackingId;

    const filtered = useMemo(() => {
        if (!search) return prefinancements;
        const q = search.toLowerCase();
        return prefinancements.filter(p =>
            (p.numeroPrefinancement || '').toLowerCase().includes(q)
            || (p.motifDemande || '').toLowerCase().includes(q)
            || (p.createdBy || '').toLowerCase().includes(q)
        );
    }, [prefinancements, search]);

    const load = async () => {
        if (!sinistreId) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/v1/prefinancements/sinistre/${sinistreId}`);
            setPrefinancements(res.data?.data || []);
        } catch (e) {
            toast.warn(t('prefinancements.erreurChargement'));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-line */ }, [sinistreId]);

    const refreshSelected = async () => {
        await load();
        if (selected?.prefinancementTrackingId) {
            try {
                const res = await axiosInstance.get(`/v1/prefinancements/${selected.prefinancementTrackingId}`);
                setSelected(res.data?.data || null);
            } catch { /* ignore */ }
        }
    };

    const totaux = useMemo(() => {
        return prefinancements.reduce((acc, p) => {
            if (p.statut !== 'ANNULE') {
                acc.prefin += Number(p.montantPrefinance || 0);
                acc.rembourse += Number(p.montantRembourse || 0);
            }
            return acc;
        }, { prefin: 0, rembourse: 0 });
    }, [prefinancements]);
    const reste = totaux.prefin - totaux.rembourse;

    const ouvrirDetail = (p) => { setSelected(p); setShowDetail(true); };

    //  Export helpers (PDF / Excel / CSV) ─
    const sinistreNumeroAffichable = sinistre?.numeroSinistreLocal
        || sinistre?.numeroSinistreManuel
        || sinistre?.sinistreReference
        || sinistre?.sinistreTrackingId?.slice(0, 8) || '';


    const cols = [
        {
            title: t('prefinancements.colonnes.numeroOperation'),
            dataIndex: 'numeroPrefinancement',
            render: (v, row) => v
                ? <Tag color="purple" style={{ fontFamily: 'var(--font-main)' }}>{v}</Tag>
                : <span style={{ color: '#9ca3af', fontSize: 12 }}>{t('prefinancements.colonnes.nonValide')}</span>,
        },
        {
            title: t('prefinancements.colonnes.statut'),
            dataIndex: 'statut',
            render: s => <Tag color={STATUT_PREFINANCEMENT_COLORS[s]}>{STATUT_PREFINANCEMENT_LABELS[s] || s}</Tag>,
        },
        { title: t('prefinancements.colonnes.montant'), dataIndex: 'montantPrefinance', align: 'right', render: m => formatMontant(m) },
        { title: t('prefinancements.colonnes.resteARembourser'), dataIndex: 'resteARembourser', align: 'right', render: m => formatMontant(m) },
        { title: t('prefinancements.colonnes.dateDemande'), dataIndex: 'datePrefinancement', render: formatDate },
        { title: t('prefinancements.colonnes.dateValidation'), dataIndex: 'dateValidation', render: d => d ? formatDate(d) : '-' },
        { title: t('prefinancements.colonnes.demandeur'), dataIndex: 'createdBy' },
        {
            title: t('prefinancements.colonnes.actions'), key: 'actions', width: 220,
            render: (_, row) => (
                <Space size="small">
                    <Tooltip title={t('prefinancements.actions.voirDetails')}>
                        <Button size="small" icon={<EyeOutlined />} onClick={() => ouvrirDetail(row)} />
                    </Tooltip>
                    {row.statut === 'DEMANDE' && canComptable && (
                        <Tooltip title={t('prefinancements.actions.validerDemande')}>
                            <Button size="small" type="primary" icon={<CheckCircleOutlined />}
                                onClick={() => setShowValider(row)} />
                        </Tooltip>
                    )}
                    {(row.statut === 'VALIDE' || row.statut === 'REMBOURSE_PARTIEL') && canComptable && (
                        <Tooltip title={t('prefinancements.actions.rembourser')}>
                            <Button size="small" type="primary" icon={<DollarOutlined />}
                                style={{ background: '#059669', borderColor: '#059669' }}
                                onClick={() => setShowRembourser(row)} />
                        </Tooltip>
                    )}
                    {(row.statut === 'DEMANDE' || row.statut === 'VALIDE') && (
                        <Tooltip title={t('prefinancements.actions.annuler')}>
                            <Button size="small" danger icon={<StopOutlined />}
                                onClick={() => setShowAnnuler(row)} />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 16 }}>
            {/* Section 1 : Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Typography.Title level={4} style={{ margin: 0 }}>{t('prefinancements.titre')}</Typography.Title>
                <Space>
                    {canRedacteur && (
                        <Tooltip title={!isDossierMurOuPlus(sinistre?.statut)
                            ? t('prefinancements.dossierNonMur', { statut: STATUT_MAP[sinistre?.statut]?.label || sinistre?.statut || '-' })
                            : ''}>
                            <Button type="primary" icon={<PlusOutlined />}
                                disabled={!isDossierMurOuPlus(sinistre?.statut)}
                                style={isDossierMurOuPlus(sinistre?.statut)
                                    ? { background: '#1a6b3c', borderColor: '#1a6b3c' }
                                    : {}}
                                onClick={() => setShowCreer(true)}>
                                {t('prefinancements.actions.nouveau')}
                            </Button>
                        </Tooltip>
                    )}
                    <Tooltip title={t('prefinancements.actions.actualiser')}>
                        <Button icon={<ReloadOutlined />} onClick={load} />
                    </Tooltip>
                </Space>
            </div>

            {/* Section 2 : KPI cards */}
            <KpiCards
                items={[
                    { label: t('prefinancements.kpi.totalPrefinance'), value: formatMontant(totaux.prefin), color: '#1a6b3c', bg: '#f0fdf4' },
                    { label: t('prefinancements.kpi.totalRembourse'), value: formatMontant(totaux.rembourse), color: '#059669', bg: '#ecfdf5' },
                    { label: t('prefinancements.kpi.resteARembourser'), value: formatMontant(reste), color: reste > 0 ? '#dc2626' : '#9ca3af', bg: reste > 0 ? '#fef2f2' : '#f3f4f6' },
                ]}
            />

            {/* Section 3 : Search bar */}
            <div style={{ marginBottom: 16 }}>
                <Input
                    prefix={<SearchOutlined />}
                    placeholder={t('prefinancements.recherche')}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    allowClear
                />
            </div>

            {/* Section 4 : Liste */}
            {loading ? (
                <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>
            ) : (
                <Table
                    rowKey="prefinancementTrackingId"
                    dataSource={filtered}
                    columns={cols}
                    size="small"
                    pagination={{ pageSize: 10, showSizeChanger: false }}
                    locale={{ emptyText: <Empty description={search ? t('prefinancements.aucunResultat') : t('prefinancements.aucunPrefinancement')} /> }}
                />
            )}

            <Drawer
                title={t('prefinancements.detail.titre')}
                open={showDetail}
                onClose={() => setShowDetail(false)}
                width={600}
            >
                {selected && (
                    <>
                        <Tag color={STATUT_PREFINANCEMENT_COLORS[selected.statut]}>
                            {STATUT_PREFINANCEMENT_LABELS[selected.statut]}
                        </Tag>
                        <Descriptions column={1} size="small" style={{ marginTop: 16 }}>
                            <Descriptions.Item label={t('prefinancements.colonnes.numeroOperation')}>{selected.numeroPrefinancement || '-'}</Descriptions.Item>
                            <Descriptions.Item label={t('prefinancements.colonnes.montantPrefinance')}>{formatMontant(selected.montantPrefinance)}</Descriptions.Item>
                            <Descriptions.Item label={t('prefinancements.colonnes.montantRembourse')}>{formatMontant(selected.montantRembourse)}</Descriptions.Item>
                            <Descriptions.Item label={t('prefinancements.colonnes.resteARembourser')}>{formatMontant(selected.resteARembourser)}</Descriptions.Item>
                            <Descriptions.Item label={t('prefinancements.colonnes.dateDemande')}>{formatDate(selected.datePrefinancement)}</Descriptions.Item>
                            <Descriptions.Item label={t('prefinancements.colonnes.dateValidation')}>{selected.dateValidation ? formatDate(selected.dateValidation) : '-'}</Descriptions.Item>
                            <Descriptions.Item label={t('prefinancements.detail.motifDemande')}>{selected.motifDemande || '-'}</Descriptions.Item>
                            {selected.statut === 'ANNULE' && (
                                <Descriptions.Item label={t('prefinancements.detail.motifAnnulation')}>{selected.motifAnnulation || '-'}</Descriptions.Item>
                            )}
                        </Descriptions>

                        {selected.remboursements?.length > 0 && (
                            <>
                                <Divider>{t('prefinancements.detail.historique')}</Divider>
                                {selected.remboursements.map(r => (
                                    <Card key={r.remboursementTrackingId} size="small" style={{ marginBottom: 8 }}>
                                        <Typography.Text strong>{formatMontant(r.montant)}</Typography.Text>
                                        {' • '}{r.encaissementNumeroCheque} • {formatDate(r.dateRemboursement)}
                                        <div style={{ fontSize: 11, color: '#6b7280' }}>
                                            {t('prefinancements.detail.validePar', { login: r.valideeParLogin || '-' })}
                                        </div>
                                    </Card>
                                ))}
                            </>
                        )}
                    </>
                )}
            </Drawer>

            {/* Bouton export flottant (bas-à-droite) */}
            <TableExportButton
                floating
                data={filtered}
                columns={[
                    { title: t('prefinancements.colonnes.numeroOperation'), dataIndex: 'numeroPrefinancement', export: (v) => v || t('prefinancements.colonnes.nonValide') },
                    { title: t('prefinancements.colonnes.statut'), dataIndex: 'statut', export: (v) => STATUT_PREFINANCEMENT_LABELS[v] || v || '' },
                    { title: t('prefinancements.colonnes.montantPrefinance'), dataIndex: 'montantPrefinance', format: 'currency', numeric: true },
                    { title: t('prefinancements.colonnes.montantRembourse'), dataIndex: 'montantRembourse', format: 'currency', numeric: true },
                    { title: t('prefinancements.colonnes.resteARembourser'), dataIndex: 'resteARembourser', format: 'currency', numeric: true },
                    { title: t('prefinancements.colonnes.dateDemande'), dataIndex: 'datePrefinancement', format: 'date' },
                    { title: t('prefinancements.colonnes.dateValidation'), dataIndex: 'dateValidation', format: 'date' },
                    { title: t('prefinancements.colonnes.demandeur'), dataIndex: 'createdBy' },
                ]}
                filename={`prefinancements_sinistre_${sinistreNumeroAffichable.replace(/[^A-Za-z0-9]/g, '_') || 'export'}`}
                title={t('prefinancements.export.titre', { numero: sinistreNumeroAffichable })}
                count={filtered?.length}
                logo={logoLocal}
            />

            <CreerPrefinancementModal
                open={showCreer}
                sinistre={sinistre}
                onClose={() => setShowCreer(false)}
                onSuccess={() => { setShowCreer(false); load(); }}
            />
            <ValiderPrefinancementModal
                prefinancement={showValider}
                onClose={() => setShowValider(null)}
                onSuccess={() => { setShowValider(null); refreshSelected(); }}
            />
            <RembourserPrefinancementModal
                prefinancement={showRembourser}
                onClose={() => setShowRembourser(null)}
                onSuccess={() => { setShowRembourser(null); refreshSelected(); }}
            />
            <AnnulerPrefinancementModal
                prefinancement={showAnnuler}
                onClose={() => setShowAnnuler(null)}
                onSuccess={() => { setShowAnnuler(null); refreshSelected(); }}
            />
        </div>
    );
}
